import { useEffect, useState } from 'react';
import { Check, Loader2, RefreshCw, CircleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { AutosaveStatus as Status } from '@/hooks/useInvitationAutosave';

interface Props {
  status: Status;
  lastSavedAt: Date | null;
  onRetry: () => void;
}

const formatRelative = (date: Date) => {
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 5) return 'hace unos segundos';
  if (seconds < 60) return `hace ${seconds} seg`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `hace ${hours} h`;
};

const AutosaveStatus = ({ status, lastSavedAt, onRetry }: Props) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (status !== 'saved' || !lastSavedAt) return;
    const interval = setInterval(() => setTick((t) => t + 1), 15_000);
    return () => clearInterval(interval);
  }, [status, lastSavedAt]);

  if (status === 'saving') {
    return (
      <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Guardando…
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span className="text-sm text-destructive inline-flex items-center gap-2">
        <CircleAlert className="w-3.5 h-3.5" />
        Error al guardar
        <Button size="sm" variant="ghost" onClick={onRetry} className="h-7 px-2">
          <RefreshCw className="w-3.5 h-3.5 mr-1" />
          Reintentar
        </Button>
      </span>
    );
  }

  if (status === 'saved' && lastSavedAt) {
    return (
      <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
        <Check className="w-3.5 h-3.5 text-emerald-600" />
        Guardado {formatRelative(lastSavedAt)}
      </span>
    );
  }

  return null;
};

export default AutosaveStatus;
